import { useMemo, useState } from 'react';
import { createFileRoute, Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import { format, parseISO } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { EntityIndexLayout, WorkspaceShell } from '@/components/layouts';
import { DepositsListPanel } from '@/components/organisms/deposits/DepositsListPanel';
import {
  type DepositTableFilters,
  type DepositTableSortColumn,
  type DepositTableSortDir,
  DepositsTableView,
} from '@/components/organisms/deposits/DepositsTableView';
import { DepositDetailPanel } from '@/components/organisms/deposits/DepositDetailPanel';
import { useDeposits } from '@/hooks/deposits';
import { DepositStatus, DepositType } from '@/lib/api/types/billing';
import type { Deposit } from '@/types/deposit';

type DepositStatusFilter = 'ALL' | DepositStatus;
type DepositTypeFilter = 'ALL' | DepositType;

interface DepositRouteSearch {
  q?: string;
  contactId?: string;
  status?: DepositStatusFilter;
  type?: DepositTypeFilter;
  from?: string;
  to?: string;
  page?: number;
}

function readStatus(value: unknown): DepositStatusFilter {
  if (value === 'ALL') return 'ALL';
  if (typeof value === 'string' && value in DepositStatus) return value as DepositStatus;
  return 'ALL';
}

function readType(value: unknown): DepositTypeFilter {
  if (value === 'ALL') return 'ALL';
  if (typeof value === 'string' && value in DepositType) return value as DepositType;
  return 'ALL';
}

function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

function readPositiveInt(value: unknown, fallback: number): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : fallback;
}

function parseRouteDate(value?: string) {
  if (!value) return undefined;
  const date = parseISO(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

export const Route = createFileRoute('/_dashboard/dashboard/deposits')({
  validateSearch: (search: Record<string, unknown>): DepositRouteSearch => ({
    q: readOptionalString(search.q),
    contactId: readOptionalString(search.contactId),
    status: readStatus(search.status),
    type: readType(search.type),
    from: readOptionalString(search.from),
    to: readOptionalString(search.to),
    page: readPositiveInt(search.page, 1),
  }),
  component: DepositsPage,
});

function DepositsPage() {
  const router = useRouter();
  const { location } = useRouterState();
  const searchParams = Route.useSearch();
  const q = searchParams.q ?? '';
  const contactId = searchParams.contactId;
  const status = searchParams.status ?? 'ALL';
  const type = searchParams.type ?? 'ALL';
  const page = searchParams.page ?? 1;

  const [tableSort, setTableSort] = useState<{
    sortCol: DepositTableSortColumn;
    sortDir: DepositTableSortDir;
  }>({ sortCol: 'captured', sortDir: 'desc' });

  // Split-list — server-side search + status filter.
  const listQuery = useDeposits({
    contactId,
    search: q.trim() || undefined,
    status: status === 'ALL' ? undefined : status,
    page: 1,
    limit: 50,
  });
  const listDeposits = listQuery.data?.data ?? [];

  const tableFilters = useMemo<DepositTableFilters>(
    () => ({
      search: q,
      status,
      type,
      date:
        searchParams.from || searchParams.to
          ? ({
              from: parseRouteDate(searchParams.from),
              to: parseRouteDate(searchParams.to),
            } satisfies DateRange)
          : undefined,
    }),
    [q, status, type, searchParams.from, searchParams.to],
  );

  const idMatch = location.pathname.match(/\/dashboard\/deposits\/([^/]+)/);
  const selectedId = idMatch?.[1] ?? null;
  const hasChildRoute = Boolean(selectedId);

  function updateSearch(patch: Partial<DepositRouteSearch>) {
    void router.navigate({
      search: (prev) => {
        const next = { ...prev, ...patch } as Record<string, unknown>;
        if (!next.q) delete next.q;
        if (next.status === 'ALL') delete next.status;
        if (next.type === 'ALL') delete next.type;
        if (!next.from) delete next.from;
        if (!next.to) delete next.to;
        if (next.page === 1) delete next.page;
        return next as never;
      },
      replace: true,
    });
  }

  function handleTableFiltersChange(patch: Partial<DepositTableFilters>) {
    const next: Partial<DepositRouteSearch> = { page: 1 };
    if ('search' in patch) next.q = patch.search || undefined;
    if ('status' in patch) next.status = patch.status ?? 'ALL';
    if ('type' in patch) next.type = patch.type ?? 'ALL';
    if ('date' in patch) {
      next.from = patch.date?.from ? format(patch.date.from, 'yyyy-MM-dd') : undefined;
      next.to = patch.date?.to ? format(patch.date.to, 'yyyy-MM-dd') : undefined;
    }
    updateSearch(next);
  }

  function openDeposit(deposit: Deposit) {
    void router.navigate({
      to: '/dashboard/deposits/$id',
      params: { id: deposit.id },
      search: (prev) => prev as never,
    });
  }

  const listPanel = (
    <DepositsListPanel
      deposits={listDeposits}
      selectedId={selectedId}
      onSelect={openDeposit}
      isLoading={listQuery.isLoading}
      search={q}
      onSearch={(value) => updateSearch({ q: value || undefined })}
      status={status}
      onStatus={(next) => updateSearch({ status: next, page: 1 })}
      total={listQuery.data?.total ?? listDeposits.length}
    />
  );

  return (
    <WorkspaceShell>
      <EntityIndexLayout
        title="Deposits"
        storageKey="deposits-view"
        hasChildRoute={hasChildRoute}
        onResetToIndex={() =>
          void router.navigate({ to: '/dashboard/deposits', search: (prev) => prev as never })
        }
        splitWidthClass="w-[340px]"
        tableSheetWidthClass="w-[620px] sm:max-w-[620px]"
        viewToggleShowLabels
        viewToggleActiveTone="muted"
        splitList={() => listPanel}
        splitDetail={hasChildRoute ? <Outlet /> : <DepositDetailPanel deposit={undefined} />}
        tableView={({ openInSheet }) => (
          <DepositsTableView
            onOpenDeposit={(deposit) => openInSheet(() => openDeposit(deposit))}
            contactId={contactId}
            filters={tableFilters}
            onFiltersChange={handleTableFiltersChange}
            page={page}
            onPageChange={(nextPage) => updateSearch({ page: nextPage })}
            sortCol={tableSort.sortCol}
            sortDir={tableSort.sortDir}
            onSortChange={(sort) => {
              setTableSort(sort);
              updateSearch({ page: 1 });
            }}
          />
        )}
        sheetContent={<Outlet />}
        mobileContent={
          <div className="flex min-h-0 flex-1 flex-col md:hidden">
            {hasChildRoute ? <Outlet /> : listPanel}
          </div>
        }
      />
    </WorkspaceShell>
  );
}
